import {
  CheckCircle2,
  ClipboardList,
  Loader2,
  MessageCircle,
  RefreshCw,
  Save,
  Send,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AdminState } from "../components/admin/AdminState";
import { PageHeader } from "../components/admin/PageHeader";
import { SettingsPanel } from "../components/admin/SettingsPanel";
import { StatusBadge } from "../components/admin/StatusBadge";
import { adminPost, type WhatsAppConsole, useAdminQuery } from "../components/admin/adminApi";

type Notice = { tone: "critical" | "success"; text: string };

type CredentialDraft = {
  apiKey: string;
  appId: string;
  appName: string;
  environment: string;
  sourceNumber: string;
  webhookSecret: string;
};

type TemplateDraft = {
  allowCategoryChange: boolean;
  body: string;
  buttonMode: "none" | "rating_quick_replies";
  category: "UTILITY" | "MARKETING" | "AUTHENTICATION";
  example: string;
  footer: string;
  languageCode: string;
  name: string;
  sampleMediaUrl: string;
  submitToGupshup: boolean;
  templateType: "TEXT" | "IMAGE";
  vertical: string;
};

type TestDraft = {
  confirmLiveSend: boolean;
  customerName: string;
  destination: string;
  productName: string;
  productImageUrl: string;
  reviewUrl: string;
  templateId: string;
};

export const initialTemplateDraft: TemplateDraft = {
  allowCategoryChange: false,
  body: "Hi {{1}}, your order containing {{2}} has been delivered.\n\nPlease rate it to share order feedback. After rating, you can add a short comment and an optional photo or video here in WhatsApp.",
  buttonMode: "rating_quick_replies",
  category: "UTILITY",
  example: "Hi Mariam, your order containing Ceramic Planter has been delivered.\n\nPlease rate it to share order feedback. After rating, you can add a short comment and an optional photo or video here in WhatsApp.",
  footer: "Order feedback request",
  languageCode: "en_US",
  name: "whatstar_order_feedback_image_v1_0902",
  sampleMediaUrl: "https://www.buildquickbots.com/whatsapp/media/sample/jpg/sample01.jpg",
  submitToGupshup: false,
  templateType: "IMAGE",
  vertical: "customer_reviews",
};

const initialTestDraft: TestDraft = {
  confirmLiveSend: false,
  customerName: "Mariam",
  destination: "",
  productName: "Demo product",
  productImageUrl: "https://www.buildquickbots.com/whatsapp/media/sample/jpg/sample01.jpg",
  reviewUrl: "https://whatstar.mojo-digital.me/review/test",
  templateId: "review_request",
};

export function CampaignPage({ channel }: { channel: "WhatsApp" | "Email" }) {
  if (channel === "Email") return <EmailCampaignPage channel={channel} />;
  return <WhatsAppCampaignPage />;
}

function EmailCampaignPage({ channel }: { channel: "Email" }) {
  return (
    <div className="wr-page">
      <PageHeader
        eyebrow="Email fallback"
        title={`${channel} campaigns`}
        description={`${channel} templates, reminders, quiet hours, test sends, and callback health.`}
        meta={
          <>
            <span>Fallback channel</span>
            <span>Reminder-ready</span>
            <span>Coupon follow-up</span>
          </>
        }
      />
      <section className="wr-grid-two">
        <SettingsPanel group={`${channel} campaigns`} />
        <aside className="wr-preview">
          <h2>Customer preview</h2>
          <div className="wr-phone">
            <p>Hi Mariam, how did Demo Product work out?</p>
            <a>Share your review and unlock your thank-you code.</a>
          </div>
        </aside>
      </section>
    </div>
  );
}

function WhatsAppCampaignPage() {
  const consoleQuery = useAdminQuery<WhatsAppConsole>("/api/admin/whatsapp");
  const [credentialDraft, setCredentialDraft] = useState({
    apiKey: "",
    appId: "",
    appName: "",
    environment: "sandbox",
    sourceNumber: "",
    webhookSecret: "",
  });
  const [templateDraft, setTemplateDraft] = useState<TemplateDraft>(initialTemplateDraft);
  const [testDraft, setTestDraft] = useState<TestDraft>(initialTestDraft);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>();

  const selectedTemplate = useMemo(
    () => consoleQuery.data?.templates.find((template) => template.id === testDraft.templateId),
    [consoleQuery.data?.templates, testDraft.templateId],
  );
  const previewBody = selectedTemplate?.body || templateDraft.body;
  const previewTemplateType = selectedTemplate?.type || templateDraft.templateType;

  useEffect(() => {
    const consoleData = consoleQuery.data;
    if (!consoleData) return;
    const activeTemplate = consoleData.templates.find((template) =>
      template.id === consoleData.settings.templateName &&
      template.source === "gupshup" &&
      template.status.toUpperCase() === "APPROVED",
    );
    if (activeTemplate) setTestDraft((current) => current.templateId === activeTemplate.id ? current : { ...current, templateId: activeTemplate.id });
  }, [consoleQuery.data]);

  async function runAction<T>(action: string, task: () => Promise<T>, success: string) {
    setBusyAction(action);
    setNotice(undefined);
    try {
      await task();
      setNotice({ tone: "success", text: success });
      await consoleQuery.reload();
    } catch (error) {
      setNotice({ tone: "critical", text: error instanceof Error ? error.message : "WhatsApp action failed." });
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div className="wr-page wr-campaign-page">
      <PageHeader
        eyebrow="WhatsApp command center"
        title="WhatsApp campaigns"
        description="Connect Gupshup, manage approved review templates, test sends, and watch delivery callbacks."
        meta={
          <>
            <span>Gupshup connected</span>
            <span>Template approval workflow</span>
            <span>Conversation tracking</span>
          </>
        }
        action={
          <div className="wr-header-actions">
            <button className="wr-button wr-button--secondary" type="button" onClick={() => void consoleQuery.reload()}>
              <RefreshCw size={15} aria-hidden />
              Refresh
            </button>
            <button
              className="wr-button"
              type="button"
              disabled={busyAction === "validate"}
              onClick={() =>
                void runAction(
                  "validate",
                  () => adminPost("/api/admin/whatsapp/validate", {}),
                  "Gupshup validation finished.",
                )
              }
            >
              {busyAction === "validate" ? <Loader2 className="wr-spin" size={15} aria-hidden /> : <ShieldCheck size={15} aria-hidden />}
              Validate
            </button>
          </div>
        }
      />

      {notice ? (
        <div className={`wr-notice wr-notice--${notice.tone}`} role="status">
          {notice.text}
        </div>
      ) : null}

      <AdminState
        status={consoleQuery.status}
        error={consoleQuery.error}
        onRetry={() => void consoleQuery.reload()}
        emptyText="Install the app on a Shopify store before configuring WhatsApp."
      >
        <section className="wr-whatsapp-layout">
          <div className="wr-whatsapp-stack">
            <ConnectionPanel
              consoleData={consoleQuery.data}
              draft={credentialDraft}
              onDraftChange={setCredentialDraft}
              busy={busyAction === "credentials"}
              onSave={() =>
                void runAction(
                  "credentials",
                  () => adminPost("/api/admin/whatsapp/credentials", credentialDraft),
                  "Gupshup credentials saved.",
                )
              }
            />

            <TemplatePanel
              consoleData={consoleQuery.data}
              draft={templateDraft}
              onDraftChange={setTemplateDraft}
              syncBusy={busyAction === "template-sync"}
              saveBusy={busyAction === "template-save"}
              activateBusy={busyAction === "template-activate"}
              onSync={() =>
                void runAction(
                  "template-sync",
                  () => adminPost("/api/admin/whatsapp/templates/sync", {}),
                  "Templates synced from Gupshup.",
                )
              }
              onSave={() =>
                void runAction(
                  "template-save",
                  () => adminPost("/api/admin/whatsapp/templates", templateDraft),
                  templateDraft.submitToGupshup ? "Template submitted to Gupshup." : "Template draft saved.",
                )
              }
              onActivate={(templateId) =>
                void runAction(
                  "template-activate",
                  () => adminPost("/api/admin/whatsapp/templates/activate", { templateId }),
                  "Approved template activated for new review requests.",
                )
              }
            />

            <TestSendPanel
              consoleData={consoleQuery.data}
              draft={testDraft}
              onDraftChange={setTestDraft}
              busy={busyAction === "test-send"}
              onSend={() =>
                void runAction(
                  "test-send",
                  () => adminPost("/api/admin/whatsapp/test-send", testDraft),
                  "WhatsApp test send submitted.",
                )
              }
            />

            <OperationsPanel consoleData={consoleQuery.data} />
          </div>

          <aside className="wr-preview wr-preview--sticky">
            <h2>Customer preview</h2>
            <div className="wr-phone wr-phone--whatsapp">
              <span>WhatsApp</span>
              {previewTemplateType === "IMAGE" ? <img src={templateDraft.sampleMediaUrl} alt="Product template sample" /> : null}
              <p>{renderPreviewBody(previewBody, testDraft)}</p>
              <a>{testDraft.reviewUrl}</a>
            </div>
            <div className="wr-webhook-copy">
              <strong>Callback URL</strong>
              <code>{consoleQuery.data?.config.webhookUrl}</code>
            </div>
          </aside>
        </section>
      </AdminState>
    </div>
  );
}

function ConnectionPanel({
  busy,
  consoleData,
  draft,
  onDraftChange,
  onSave,
}: {
  busy: boolean;
  consoleData?: WhatsAppConsole;
  draft: CredentialDraft;
  onDraftChange: (draft: CredentialDraft) => void;
  onSave: () => void;
}) {
  const credentials = Object.entries(consoleData?.config.credentials ?? {});
  const readyCount = consoleData?.config.required.filter((check) => check.configured).length ?? 0;

  return (
    <section className="wr-panel wr-whatsapp-section">
      <div className="wr-section-heading">
        <div>
          <h2>Connection</h2>
          <p>{readyCount} of {consoleData?.config.required.length ?? 3} send requirements ready</p>
        </div>
        <StatusBadge tone={consoleData?.status.sendReady ? "success" : "warning"}>
          {consoleData?.status.sendReady ? "Ready to send" : "Needs setup"}
        </StatusBadge>
      </div>
      <div className="wr-credential-grid">
        {credentials.map(([key, credential]) => (
          <div key={key} className="wr-credential-tile">
            <span>{credential.label}</span>
            <strong>{credential.configured ? credential.value || "Saved" : "Missing"}</strong>
            <small>{credential.source}</small>
          </div>
        ))}
      </div>
      <div className="wr-form-grid">
        <Field label="App name">
          <input value={draft.appName} onChange={(event) => onDraftChange({ ...draft, appName: event.target.value })} placeholder="Gupshup app name" />
        </Field>
        <Field label="App ID">
          <input value={draft.appId} onChange={(event) => onDraftChange({ ...draft, appId: event.target.value })} placeholder="Gupshup app id" />
        </Field>
        <Field label="Source number">
          <input value={draft.sourceNumber} onChange={(event) => onDraftChange({ ...draft, sourceNumber: event.target.value })} placeholder="15551234567" />
        </Field>
        <Field label="Environment">
          <select value={draft.environment} onChange={(event) => onDraftChange({ ...draft, environment: event.target.value })}>
            <option value="sandbox">Sandbox</option>
            <option value="production">Production</option>
          </select>
        </Field>
        <Field label="API key">
          <input value={draft.apiKey} onChange={(event) => onDraftChange({ ...draft, apiKey: event.target.value })} placeholder="Paste to save or rotate" />
        </Field>
        <Field label="Webhook secret">
          <input value={draft.webhookSecret} onChange={(event) => onDraftChange({ ...draft, webhookSecret: event.target.value })} placeholder="Optional provider secret" />
        </Field>
      </div>
      <button className="wr-button" type="button" disabled={busy} onClick={onSave}>
        {busy ? <Loader2 className="wr-spin" size={15} aria-hidden /> : <Save size={15} aria-hidden />}
        Save connection
      </button>
    </section>
  );
}

function TemplatePanel({
  consoleData,
  draft,
  onDraftChange,
  onSave,
  onSync,
  onActivate,
  activateBusy,
  saveBusy,
  syncBusy,
}: {
  consoleData?: WhatsAppConsole;
  draft: TemplateDraft;
  onDraftChange: (draft: TemplateDraft) => void;
  onSave: () => void;
  onSync: () => void;
  onActivate: (templateId: string) => void;
  activateBusy: boolean;
  saveBusy: boolean;
  syncBusy: boolean;
}) {
  return (
    <section className="wr-panel wr-whatsapp-section">
      <div className="wr-section-heading">
        <div>
          <h2>Templates</h2>
          <p>Initial: {consoleData?.settings.templateName ?? "review_request"} · Reminder: {consoleData?.settings.reminderTemplateName ?? "review_request_reminder"} · Type: {consoleData?.settings.templateType ?? "TEXT"}</p>
        </div>
        <button className="wr-button wr-button--secondary" type="button" disabled={syncBusy} onClick={onSync}>
          {syncBusy ? <Loader2 className="wr-spin" size={15} aria-hidden /> : <RefreshCw size={15} aria-hidden />}
          Sync
        </button>
      </div>
      <div className="wr-template-list">
        {(consoleData?.templates.length ? consoleData.templates : []).map((template) => (
          <article key={template.id} className="wr-template-card">
            <div>
              <strong>{template.name}</strong>
              <StatusBadge tone={template.status === "APPROVED" ? "success" : template.status === "REJECTED" ? "critical" : "warning"}>
                {template.status}
              </StatusBadge>
            </div>
            <p>{template.body || "No body returned by provider."}</p>
            {template.buttons?.length ? (
              <div className="wr-template-buttons">
                {template.buttons.map((button) => (
                  <span key={`${template.id}:${button.text}`}>{button.text}</span>
                ))}
              </div>
            ) : null}
            <small>{template.category} · {template.type} · {template.language || "default"} · {template.source} · {consoleData?.settings.templateName === template.id ? "active" : "not active"}</small>
            {template.source === "gupshup" && template.status.toUpperCase() === "APPROVED" && template.category.toUpperCase() === "UTILITY" && template.type.toUpperCase() === "IMAGE" && consoleData?.settings.templateName !== template.id ? (
              <button className="wr-button wr-button--secondary" type="button" disabled={activateBusy} onClick={() => onActivate(template.id)}>
                {activateBusy ? <Loader2 className="wr-spin" size={15} aria-hidden /> : null}
                Activate for new requests
              </button>
            ) : null}
            {template.source === "gupshup" && template.status.toUpperCase() === "APPROVED" && (template.category.toUpperCase() !== "UTILITY" || template.type.toUpperCase() !== "IMAGE") ? <small>Only approved UTILITY image templates can be activated.</small> : null}
            {template.status.toUpperCase() !== "APPROVED" ? <small>Sync after Meta approval before activating.</small> : null}
          </article>
        ))}
        {!consoleData?.templates.length ? <div className="wr-empty-inline">No templates synced or drafted yet.</div> : null}
      </div>
      <div className="wr-form-grid">
        <Field label="Template name">
          <input value={draft.name} onChange={(event) => onDraftChange({ ...draft, name: event.target.value })} />
        </Field>
        <Field label="Category">
          <select value={draft.category} onChange={(event) => onDraftChange({ ...draft, category: event.target.value as TemplateDraft["category"] })}>
            <option value="UTILITY">Utility</option>
            <option value="MARKETING">Marketing</option>
            <option value="AUTHENTICATION">Authentication</option>
          </select>
        </Field>
        <Field label="Template type">
          <select value={draft.templateType} onChange={(event) => onDraftChange({ ...draft, templateType: event.target.value as TemplateDraft["templateType"] })}>
            <option value="IMAGE">Image header</option>
            <option value="TEXT">Text only</option>
          </select>
        </Field>
        <Field label="Language">
          <input value={draft.languageCode} onChange={(event) => onDraftChange({ ...draft, languageCode: event.target.value })} />
        </Field>
        <Field label="Vertical">
          <input value={draft.vertical} onChange={(event) => onDraftChange({ ...draft, vertical: event.target.value })} />
        </Field>
        <Field label="Template buttons">
          <select
            value={draft.buttonMode}
            onChange={(event) => onDraftChange({ ...draft, buttonMode: event.target.value as TemplateDraft["buttonMode"] })}
          >
            <option value="rating_quick_replies">Rating quick replies</option>
            <option value="none">No buttons</option>
          </select>
        </Field>
      </div>
      <Field label="Body">
        <textarea rows={4} value={draft.body} onChange={(event) => onDraftChange({ ...draft, body: event.target.value })} />
      </Field>
      <Field label="Sample">
        <textarea rows={3} value={draft.example} onChange={(event) => onDraftChange({ ...draft, example: event.target.value })} />
      </Field>
      {draft.templateType === "IMAGE" ? (
        <Field label="Sample image URL">
          <input value={draft.sampleMediaUrl} onChange={(event) => onDraftChange({ ...draft, sampleMediaUrl: event.target.value })} placeholder="https://example.com/product.jpg" />
        </Field>
      ) : null}
      <div className="wr-form-grid">
        <Field label="Footer">
          <input value={draft.footer} onChange={(event) => onDraftChange({ ...draft, footer: event.target.value })} />
        </Field>
        <label className="wr-checkbox wr-inline-checkbox">
          <input
            checked={draft.submitToGupshup}
            type="checkbox"
            onChange={(event) => onDraftChange({ ...draft, submitToGupshup: event.target.checked })}
          />
          <span>Submit to Gupshup for Meta approval</span>
        </label>
      </div>
      <button className="wr-button" type="button" disabled={saveBusy} onClick={onSave}>
        {saveBusy ? <Loader2 className="wr-spin" size={15} aria-hidden /> : <ClipboardList size={15} aria-hidden />}
        {draft.submitToGupshup ? "Submit template" : "Save draft"}
      </button>
    </section>
  );
}

function TestSendPanel({
  busy,
  consoleData,
  draft,
  onDraftChange,
  onSend,
}: {
  busy: boolean;
  consoleData?: WhatsAppConsole;
  draft: TestDraft;
  onDraftChange: (draft: TestDraft) => void;
  onSend: () => void;
}) {
  const templateOptions = consoleData?.templates.filter((template) => template.source === "gupshup" && template.status.toUpperCase() === "APPROVED") ?? [];
  const needsConfirm = Boolean(consoleData?.status.sendReady);

  return (
    <section className="wr-panel wr-whatsapp-section">
      <div className="wr-section-heading">
        <div>
          <h2>Test send</h2>
          <p>{needsConfirm ? "Uses live Gupshup credentials." : "Runs as a dry-run until credentials are complete."}</p>
        </div>
        <MessageCircle size={20} aria-hidden />
      </div>
      <div className="wr-form-grid">
        <Field label="Recipient phone">
          <input value={draft.destination} onChange={(event) => onDraftChange({ ...draft, destination: event.target.value })} placeholder="15557654321" />
        </Field>
        <Field label="Template">
          <select value={draft.templateId} onChange={(event) => onDraftChange({ ...draft, templateId: event.target.value })}>
            {templateOptions.map((template) => (
              <option key={template.id} value={template.id}>{template.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Customer">
          <input value={draft.customerName} onChange={(event) => onDraftChange({ ...draft, customerName: event.target.value })} />
        </Field>
        <Field label="Product">
          <input value={draft.productName} onChange={(event) => onDraftChange({ ...draft, productName: event.target.value })} />
        </Field>
      </div>
      <Field label="Review link">
        <input value={draft.reviewUrl} onChange={(event) => onDraftChange({ ...draft, reviewUrl: event.target.value })} />
      </Field>
      <Field label="Product image URL (required for image templates)">
        <input value={draft.productImageUrl} onChange={(event) => onDraftChange({ ...draft, productImageUrl: event.target.value })} placeholder="https://example.com/product.jpg" />
      </Field>
      {needsConfirm ? (
        <label className="wr-checkbox wr-inline-checkbox">
          <input
            checked={draft.confirmLiveSend}
            type="checkbox"
            onChange={(event) => onDraftChange({ ...draft, confirmLiveSend: event.target.checked })}
          />
          <span>I understand this sends a real WhatsApp template message</span>
        </label>
      ) : null}
      <button className="wr-button" type="button" disabled={busy || !templateOptions.some((template) => template.id === draft.templateId) || (needsConfirm && !draft.confirmLiveSend)} onClick={onSend}>
        {busy ? <Loader2 className="wr-spin" size={15} aria-hidden /> : <Send size={15} aria-hidden />}
        Send test request
      </button>
    </section>
  );
}

function OperationsPanel({ consoleData }: { consoleData?: WhatsAppConsole }) {
  return (
    <section className="wr-panel wr-whatsapp-section">
      <div className="wr-section-heading">
        <div>
          <h2>Operations</h2>
          <p>Last message: {consoleData?.status.lastMessageAt ? formatDate(consoleData.status.lastMessageAt) : "None"} · Last callback: {consoleData?.status.lastWebhookAt ? formatDate(consoleData.status.lastWebhookAt) : "None"}</p>
        </div>
        <CheckCircle2 size={20} aria-hidden />
      </div>
      <div className="wr-ops-grid">
        <div>
          <h3>Recent messages</h3>
          <div className="wr-event-list">
            {consoleData?.recentMessages.map((message) => (
              <div key={message.id}>
                <StatusBadge tone={toneForStatus(message.rawStatus)}>{message.status}</StatusBadge>
                <span>{message.templateName}</span>
                <small>{formatDate(message.queuedAt)} · {message.providerMessageId}</small>
              </div>
            ))}
            {!consoleData?.recentMessages.length ? <p>No WhatsApp messages yet.</p> : null}
          </div>
        </div>
        <div>
          <h3>Callback events</h3>
          <div className="wr-event-list">
            {consoleData?.webhookEvents.map((event) => (
              <div key={`${event.eventType}:${event.providerEventId}`}>
                <StatusBadge tone={toneForStatus(event.rawStatus)}>{event.status}</StatusBadge>
                <span>{event.eventType}</span>
                <small>{formatDate(event.receivedAt)} · {event.providerMessageId}</small>
              </div>
            ))}
            {!consoleData?.webhookEvents.length ? <p>No Gupshup callbacks received yet.</p> : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <label className="wr-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function renderPreviewBody(body: string, testDraft: TestDraft) {
  return body
    .replace(/\{\{1\}\}/g, testDraft.customerName || "Customer")
    .replace(/\{\{2\}\}/g, testDraft.productName || "your order")
    .replace(/\{\{3\}\}/g, testDraft.reviewUrl || "review link");
}

function toneForStatus(status: string) {
  if (["FAILED", "REJECTED"].includes(status)) return "critical";
  if (["SENT", "DELIVERED", "READ", "PROCESSED", "APPROVED"].includes(status)) return "success";
  if (["QUEUED", "RECEIVED", "PROCESSING", "PENDING", "SUBMITTED", "DRAFT"].includes(status)) return "warning";
  return "info";
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}
